import { useMatching } from '@/OneLiners';
import {
    ActionStatusHelper,
    useAppEnum,
    DomainFormHook,
    ActionResponse,
    useFormLifeCycle,
} from "@/Shared";


import { MatchingFormViewModel } from '../ViewModels';
import { useMatchingEnricher } from "./useMatchingEnricher";
import { DOMAIN_CONFIGS, DomainHookProps } from "@/Shared/Config/domain-config";
import { MatchingValidationSchema } from "../MatchingFormValidation";
import { MatchingModel } from "../MatchingModel";


/**
 * useMatchingForm hook – manages matching form data and interactions.
 * 
 * Author: Samano CASTRE  
 * Date: July 17, 2025
 * 
 * Responsibilities:
 * - Initialises matching form data by fetching current matching and agent list
 * - Tracks form submission state and response (create or update)
 * - Exposes form logic to the component layer
 * 
 * Behavior:
 * - On mount, fetches the current matching (if editing) and the list of agents
 * - Automatically switches between `createMatching` or `updateMatching` based on matching presence
 * - Tracks the response of the latest action to provide submission feedback
 */
export const useMatchingForm = (props: DomainHookProps): DomainFormHook => {
    const { permissionHook } = props;
    const { domainType } = DOMAIN_CONFIGS.Matching;

    /**
     * Matching domain operations and related backend state.
     * Exposes CRUD actions, current entity fetching and associated responses.
     */
    const matchingHook = useMatching();

    /**
     * Matching-related enums exposed to the UI layer.
     * Used to build the form view model.
     */
    const { MatchingEnums } = useAppEnum();

    /**
     * Matching enrichment layer.
     * Provides additional data (users, computed fields, etc.)
     * required to build the form view model.
     */
    const {
        enricherResponse,
        getEnrichedMatching,
    } = useMatchingEnricher();

    /**
     * Aggregated response representing the global initialization state.
     * Combines:
     * - entity domain state
     * - permission loading state
     * - enrichment layer state
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            matchingHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: matchingHook.stateResponse.error ??
            enricherResponse.error
    };


    const lifeCycleHook = useFormLifeCycle({
        response,
        domainType,
        props,
        entityHook: matchingHook,
        buildVM: (currentEntity: MatchingModel) => {
            return new MatchingFormViewModel(
                MatchingEnums,
                getEnrichedMatching(currentEntity)
            )
        }
    });


    /**
     * Public API exposed to the component layer.
     * Only data and actions strictly required by the UI are returned.
     */
    return {
        // Form
        ...lifeCycleHook,
        ValidationSchema: MatchingValidationSchema(MatchingEnums)
    }
}